import { Dropdown } from '@cambridgeassessment/cambridge-ui';
import { BusinessUnit, Centre } from '@cambridgeassessment/lib-ee-commons';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCentre } from '../../service/consortiaManagement';
import { RootState } from '../../store';
import { displayNotification } from '../../store/notification/actions';

interface Props {
  dataQaId: string;
  value: Centre | null;
  onChange: (value: Centre | null) => void;
  helperText?: string;
  validateCentreId?: (centreId: string) => string | undefined;
  arialLabel?: string;
}

const getCentre = async (businessId: BusinessUnit['id'], centreId: Centre['id']): Promise<Centre | null> => {
  try {
    const response = await fetchCentre(businessId, centreId);

    if (!response) {
      return null;
    }

    return {
      id: response.id,
      name: response.name,
      zone: response.zone,
      zoneId: response.zoneId,
    };
  } catch {
    return null;
  }
};

const CentreIdTextField: React.FC<Props> = ({
  dataQaId,
  value,
  onChange,
  helperText,
  validateCentreId,
  arialLabel,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [centre, setCentre] = useState<Centre | null>(null);
  const [dropDownText, setDropDownText] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { businessUnit } = useSelector((state: RootState) => state.userData);

  const onOpen = (): void => {
    setDropDownText('Enter Centre ID (5 alphanumerics)');
    setIsOpen(true);
  };

  const onClose = (): void => {
    setIsOpen(false);
    if (inputValue.length === 0) {
      onChange(null);
    }
  };

  const handleInputChange = async (newInputValue: string) => {
    setInputValue(newInputValue);
    setCentre(null);
    const centreId = newInputValue.trim().toUpperCase();

    if (centreId.length !== 5) {
      setDropDownText('Enter Centre ID (5 alphanumerics)');
      return;
    }

    const validateCentreInput = new RegExp(/^[a-zA-Z0-9]*$/);
    if (!validateCentreInput.test(centreId)) {
      dispatch(displayNotification('Only alphanumeric characters are allowed.'));
      return;
    }

    if (validateCentreId) {
      const error = validateCentreId(centreId);
      if (error) {
        setDropDownText(error);
        return;
      }
    }

    setDropDownText('Searching for centre...');

    const centreResponse = await getCentre(businessUnit?.id || '', centreId.toUpperCase());
    setCentre(centreResponse);

    if (!centreResponse) {
      setDropDownText('No results found.');
    }
  };

  const renderCentre = ({ id, name }: Centre): string => `${id} - ${name}`;

  return (
    <Dropdown
      data={centre ? [centre] : []}
      renderOption={renderCentre}
      noOptionsText={dropDownText}
      renderSelectedOption={renderCentre}
      dataQaId={dataQaId}
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={(newInputValue: string) => {
        void handleInputChange(newInputValue);
      }}
      value={value || null}
      placeholder='Enter centre ID'
      title='Select centre'
      readOnly={false}
      showPopUpIcon={false}
      open={isOpen}
      helperText={helperText}
      ariaLabel={arialLabel}
    />
  );
};

export default CentreIdTextField;
